import React from 'react';
import { Calculator, DollarSign, Download } from 'lucide-react';
import { DataTable } from '../../common/DataTable';
import Card from '../../common/Card';
import Badge from '../../common/Badge';
import EmptyState from '../../common/EmptyState';
import Button from '../../common/Button';
import Tabs from '../../common/Tabs';

// Extracted verbatim from JobManagement's documents tab block (Task 7),
// restyled to B2 primitives (Task 6 of the mechanic UI restyle).
// Presentational only — loadDocuments is driven by the orchestrator's
// tab/paging effect, which is why this tab does not receive it: changing
// documentsSubTab or documentsPage re-triggers that effect.
//
// Invoice status tone map is local to this file — parts/tones.js's
// STATUS_TONE covers job statuses (created/diagnosing/...), not invoice
// statuses (paid/sent/pending/overdue), so it is a different vocabulary
// and is not imported from there.
const STATUS_TONE = { paid: 'success', sent: 'info', pending: 'warning', overdue: 'danger' };

const DocumentsTab = ({
  allInvoices,
  allEstimates,
  documentsSubTab,
  setDocumentsSubTab,
  documentsPage,
  setDocumentsPage,
  documentsTotalPages,
  documentsLoading,
  downloadSavedInvoicePDF
}) => {
  const invoiceColumns = [
    {
      header: 'Invoice #',
      accessor: 'invoice_number',
      render: (r) => <span className="font-mono tabular-nums text-ink font-medium">{r.invoice_number}</span>,
    },
    {
      header: 'Job',
      accessor: 'job_number',
      render: (r) => <span className="font-mono tabular-nums text-ink-soft">{r.job_number}</span>,
    },
    { header: 'Customer', accessor: 'customer_name' },
    {
      header: 'Vehicle',
      accessor: 'vehicle_info',
      render: (r) => <span className="text-ink-soft">{r.vehicle_info || '—'}</span>,
    },
    {
      header: 'Total',
      accessor: 'total',
      numeric: true,
      render: (r) => <span className="font-mono tabular-nums text-ink">${Number(r.total || 0).toFixed(2)}</span>,
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (r) => <Badge status={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge>,
    },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (r) => (
        <span className="font-mono tabular-nums text-ink-soft">
          {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      sortable: false,
      render: (r) => (
        <Button variant="ghost" size="sm" onClick={() => downloadSavedInvoicePDF(r)}>
          <Download size={16} strokeWidth={1.5} />
        </Button>
      ),
    },
  ];

  const renderInvoiceCard = (r) => (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono tabular-nums text-ink font-medium">{r.invoice_number}</span>
        <Badge status={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge>
      </div>
      <div className="mt-1 text-small text-ink-muted">
        {r.customer_name} &middot; {r.vehicle_info || '—'}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono tabular-nums text-section-title text-ink">
          ${Number(r.total || 0).toFixed(2)}
        </span>
        <Button
          variant="secondary"
          size="sm"
          className="min-h-touch"
          onClick={() => downloadSavedInvoicePDF(r)}
        >
          <Download size={16} strokeWidth={1.5} className="mr-1" />PDF
        </Button>
      </div>
    </Card>
  );

  // An estimate's latest recorded approval, or a badge saying there is none.
  const approvedCell = (est) => (
    est.latest_authorized_amount == null
      ? <Badge status="warning">Not approved</Badge>
      : (
        <span className="font-mono tabular-nums text-ink">
          ${Number(est.latest_authorized_amount).toFixed(2)}
        </span>
      )
  );

  const estimateColumns = [
    {
      header: 'Estimate #',
      accessor: 'invoice_number',
      render: (est) => <span className="font-mono tabular-nums text-ink font-medium">{est.invoice_number}</span>,
    },
    {
      header: 'Job',
      accessor: 'job_number',
      render: (est) => <span className="font-mono tabular-nums text-ink-soft">{est.job_number}</span>,
    },
    { header: 'Customer', accessor: 'customer_name' },
    {
      header: 'Vehicle',
      accessor: 'vehicle_info',
      render: (est) => <span className="text-ink-soft">{est.vehicle_info || '—'}</span>,
    },
    {
      header: 'Total',
      accessor: 'total',
      numeric: true,
      render: (est) => <span className="font-mono tabular-nums text-ink">${Number(est.total || 0).toFixed(2)}</span>,
    },
    { header: 'Approved', accessor: 'latest_authorized_amount', render: approvedCell },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (est) => (
        <span className="font-mono tabular-nums text-ink-soft">
          {est.created_at ? new Date(est.created_at).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      sortable: false,
      render: (est) => (
        <Button variant="ghost" size="sm" onClick={() => downloadSavedInvoicePDF(est)}>
          <Download size={16} strokeWidth={1.5} />
        </Button>
      ),
    },
  ];

  const renderEstimateCard = (est) => (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono tabular-nums text-ink font-medium">{est.invoice_number}</span>
        {approvedCell(est)}
      </div>
      <div className="mt-1 text-small text-ink-muted">
        {est.customer_name} &middot; {est.vehicle_info || '—'}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono tabular-nums text-section-title text-ink">
          ${Number(est.total || 0).toFixed(2)}
        </span>
        <Button
          variant="secondary"
          size="sm"
          className="min-h-touch"
          onClick={() => downloadSavedInvoicePDF(est)}
        >
          <Download size={16} strokeWidth={1.5} className="mr-1" />PDF
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="bg-surface-card rounded-card shadow-elevation-1">
        <div className="px-4 sm:px-6 pt-4 sm:pt-6">
          <Tabs
            tabs={[
              { key: 'invoices', label: 'Invoices', icon: DollarSign },
              { key: 'estimates', label: 'Estimates', icon: Calculator },
            ]}
            activeTab={documentsSubTab}
            onChange={(key) => { setDocumentsSubTab(key); setDocumentsPage(1); }}
          />
        </div>

        <div className="p-4 sm:p-6">
          {documentsSubTab === 'invoices' ? (
            allInvoices.length === 0 && !documentsLoading ? (
              <EmptyState
                icon={DollarSign}
                title="No invoices yet"
                description="Invoices you save from a job appear here."
              />
            ) : (
              <DataTable
                data={allInvoices}
                columns={invoiceColumns}
                density="compact"
                loading={documentsLoading}
                pagination={false}
                filterable={false}
                sortable={false}
                renderCard={renderInvoiceCard}
              />
            )
          ) : (
            allEstimates.length === 0 && !documentsLoading ? (
              <EmptyState
                icon={Calculator}
                title="No estimates yet"
                description="Estimates you save from a job appear here."
              />
            ) : (
              <DataTable
                data={allEstimates}
                columns={estimateColumns}
                density="compact"
                loading={documentsLoading}
                pagination={false}
                filterable={false}
                sortable={false}
                renderCard={renderEstimateCard}
              />
            )
          )}

          {/* Pagination */}
          {documentsTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-default">
              <button
                onClick={() => setDocumentsPage(p => Math.max(1, p - 1))}
                disabled={documentsPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-ink-muted">
                Page {documentsPage} of {documentsTotalPages}
              </span>
              <button
                onClick={() => setDocumentsPage(p => Math.min(documentsTotalPages, p + 1))}
                disabled={documentsPage === documentsTotalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsTab;
